import React from 'react';
import { SafeRender, isWithin24Hours } from '../../../utils/formattingUtils';
import { getInitials } from '../../../utils/profilePictureUtils';

// Arrow icons for sorting
const SortArrow = ({ direction }) => {
  if (!direction) return null;
  
  return direction === 'asc' ? (
    <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 6.414l-3.293 3.293a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  ) : (
    <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 13.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
};

// Helper function to get profile picture with fallback
const getProfileImage = (user) => {
  // Priority: profilePicture > basicInfo.photoURL > photoURL
  return user.profilePicture || user.basicInfo?.photoURL || user.photoURL || null;
};

// User Avatar Component with initials fallback
const UserAvatar = ({ user }) => {
  const profilePic = getProfileImage(user);
  const initials = getInitials(user.displayName || user.email);
  
  if (profilePic) {
    return (
      <img 
        alt={user.displayName} 
        src={profilePic} 
        className="size-12 flex-none rounded-full bg-gray-50 dark:bg-gray-800 object-cover"
      />
    );
  }
  
  // Initials fallback
  return (
    <div className="size-12 flex-none rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
      <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
        {initials}
      </span>
    </div>
  );
};

// Helper function to get job search status display
const getJobSearchStatusDisplay = (user) => {
  const status = user.preferences?.jobSearchStatus || user.jobSearchStatus || 'not-interested';
  
  const statusMap = {
    'actively-looking': { label: 'Actively Looking', color: 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-400/20' },
    'open-to-opportunities': { label: 'Open to Opportunities', color: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-400/20' },
    'not-interested': { label: 'Not Interested', color: 'bg-gray-50 text-gray-600 ring-gray-600/20 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-400/20' }
  };
  
  return statusMap[status] || statusMap['not-interested'];
};

export default function UserListTable({ 
  users, 
  isLoading, 
  onUserSelect, 
  sortField = null, 
  sortDirection = null, 
  onSort = () => {} 
}) {
  if (isLoading) {
    return (
      <div className="px-4 py-5 sm:p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="px-4 py-5 sm:p-6 text-center">
        <p className="text-gray-500">No users found.</p>
      </div>
    );
  }

  // Define sortable columns
  const columns = [
    { key: 'displayName', label: 'Name', sortable: true, width: 'w-64' },
    { key: 'linkedinUrl', label: 'LinkedIn', sortable: false, width: 'w-48' },
    { key: 'location', label: 'Location', sortable: true, width: 'w-40' },
    { key: 'status', label: 'Status', sortable: true, width: 'w-32' },
    { key: 'jobSearchStatus', label: 'Job Search', sortable: true, width: 'w-40' },
    { key: 'lastLogin', label: 'Last Login', sortable: true, width: 'w-40' },
  ];

  return (
    <div className="mt-6 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full table-auto divide-y divide-gray-300 dark:divide-gray-700" style={{ minWidth: '1200px' }}>
            <thead>
              <tr>
                {columns.map((column, idx) => (
                  <th 
                    key={column.key}
                    scope="col" 
                    className={`${idx === 0 ? 'py-3.5 pl-4 pr-3 sm:pl-0' : 'px-3 py-3.5'} ${column.width || ''} text-left text-sm font-semibold text-gray-900 dark:text-gray-100`}
                  >
                    {column.sortable ? (
                      <button 
                        onClick={() => onSort(column.key)}
                        className="group inline-flex items-center gap-x-2 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                      >
                        {column.label}
                        {sortField === column.key ? (
                          <span className="flex-none rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/40">
                            <SortArrow direction={sortDirection} />
                          </span>
                        ) : (
                          <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.56 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.25 9.5a.75.75 0 011.1-1.02l2.7 3.2 2.7-3.2a.75.75 0 011.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {users.map((user, idx) => {
                const isNewUser = isWithin24Hours(user.createdAt);
                const userRole = user.basicInfo?.currentRole || user.currentRole || null;
                
                return (
                  <tr 
                    key={user.id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer group transition-colors"
                    onClick={() => onUserSelect(user.id)}
                  >
                    {/* Name with Avatar */}
                    <td className="py-4 pl-4 pr-3 sm:pl-0">
                      <div className="flex items-center gap-x-4">
                        <UserAvatar user={user} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {user.displayName}
                            </p>
                            {isNewUser && (
                              <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900/30 px-1.5 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300 ring-1 ring-inset ring-purple-700/10 dark:ring-purple-400/20">
                                New
                              </span>
                            )}
                          </div>
                          {userRole && (
                            <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                              {userRole}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* LinkedIn URL */}
                    <td className="px-3 py-4 text-sm">
                      {user.linkedinUrl ? (
                        <a
                          href={user.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors group/link"
                          title={user.linkedinUrl}
                        >
                          <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                          <span className="truncate max-w-[180px]">
                            {user.linkedinUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                          </span>
                          <svg className="h-3 w-3 flex-shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    
                    {/* Location */}
                    <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="line-clamp-2">
                        {user.location || '—'}
                      </div>
                    </td>
                    
                    {/* Status */}
                    <td className="px-3 py-4 text-sm whitespace-nowrap">
                      {(() => {
                        const status = user.status || 'active';
                        const statusConfig = {
                          'waitlist': { label: 'Waitlist', color: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-400/20' },
                          'invited': { label: 'Invited', color: 'bg-blue-50 text-blue-800 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-400/20' },
                          'active': { label: 'Active', color: 'bg-green-50 text-green-800 ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-400/20' }
                        };
                        const config = statusConfig[status] || statusConfig['active'];
                        return (
                          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${config.color}`}>
                            {config.label}
                          </span>
                        );
                      })()}
                    </td>
                    
                    {/* Job Search Status */}
                    <td className="px-3 py-4 text-sm whitespace-nowrap">
                      {(() => {
                        const statusDisplay = getJobSearchStatusDisplay(user);
                        return (
                          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusDisplay.color}`}>
                            {statusDisplay.label}
                          </span>
                        );
                      })()}
                    </td>
                    
                    {/* Last Login */}
                    <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {user.lastLogin && user.lastLogin !== 'Never' ? (
                        <div>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            <SafeRender value={user.lastLogin} />
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-x-1.5">
                          <div className="flex-none rounded-full bg-gray-500/20 dark:bg-gray-500/30 p-1">
                            <div className="size-1.5 rounded-full bg-gray-500 dark:bg-gray-400" />
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Never logged in</p>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 